import { inject } from "vue";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import displayErrorMessage from "@/utils/ErrorHandling";
import { getErrorMessage } from "bright-logic-core/src/errors";
import {
  SummaryStatementItem,
  SummaryStatementRequest,
} from "@/types/financials/SummaryStatement";
import {
  DeleteFeeRequest,
  OneOffFeeRequest,
} from "@/types/financials/OneOffFeeRequest";
import { SummaryStatementPdfRequest } from "@/types/financials/SummaryStatementPdfRequest";
import { PropertyContactPdfRequest } from "@/types/financials/PropertyContactPdfRequest";
import { ProcessedPaymentRun } from "@/types/accounts/ProcessedPaymentRun";
import { PaymentRunListItem } from "@/types/accounts/PaymentRunListItem";
import { PaymentRunContactAPIFilters } from "@/types/accounts/PaymentRunContactAPIFilters";
import { PaymentRunsToProcess } from "@/types/accounts/PaymentRunsToProcess ";
import {
  BulkStatementRequest,
  ConsolidatedToPayItem,
  ConsolidatedToPayquest,
  PayAccountRequest,
  PaymentAccountItem,
} from "@/types/financials/ConsolidatedPayment";

export function useFinancialHelper() {
  const acquaintApiClient = inject<AcquaintApiClient>("AcquaintApiClient")!;

  return {
    getSummaryStatements: async (request: SummaryStatementRequest) => {
      try {
        return await acquaintApiClient.httpPost<SummaryStatementItem[]>(
          `app/summarystatements/getsummarystatements`,
          request,
        );
      } catch (ex) {
        displayErrorMessage(getErrorMessage(ex));
      }
    },

    createOneOffFee: async (request: OneOffFeeRequest) => {
      try {
        await acquaintApiClient.httpPostVoid(
          `app/summarystatements/createoneofffee`,
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    deleteTransactionBatch: async (request: DeleteFeeRequest) => {
      try {
        await acquaintApiClient.httpPostVoid(
          `app/summarystatements/DeleteTransactionBatch`,
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    generatePdfAllSummaryStatements: async (
      request: SummaryStatementPdfRequest,
    ) => {
      try {
        return await acquaintApiClient.getFileWithPostData(
          `app/summarystatements/generateallsummarystatementspdf`,
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    generatePdfForpropertycontact: async (
      request: PropertyContactPdfRequest,
    ) => {
      try {
        return await acquaintApiClient.getFileWithPostData(
          `app/summarystatements/generatesummarystatementforpropertycontactpdf`,
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    fetchPaymentExportFile: async (
      processedPaymentRun: ProcessedPaymentRun,
    ) => {
      try {
        return await acquaintApiClient.getFileWithPostData(
          "app/paymentruns/fetchpaymentrunfile",
          {
            paymentRunExportFileName:
              processedPaymentRun.paymentRunExportFileName,
          },
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return undefined;
      }
    },

    processPayments: async (
      paymentsToProcess: PaymentRunsToProcess,
    ): Promise<ProcessedPaymentRun | undefined> => {
      try {
        return await acquaintApiClient.httpPost(
          `app/paymentruns/processpayments`,
          paymentsToProcess,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return undefined;
      }
    },

    downloadPaymentRuns: async (
      downloadPaymentRunRequest: any,
    ): Promise<Blob | undefined> => {
      try {
        return await acquaintApiClient.getFileWithPostData(
          "app/paymentruns/downloadpaymentrunstatements",
          downloadPaymentRunRequest,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return undefined;
      }
    },

    fetchPayments: async (
      fetchPaymentsFilters: PaymentRunContactAPIFilters,
    ): Promise<PaymentRunListItem[]> => {
      try {
        return await acquaintApiClient.httpPost(
          `app/paymentruns/fetchcontactstopay`,
          fetchPaymentsFilters,
        );
      } catch (error) {
        displayErrorMessage(getErrorMessage(error));
        return [];
      }
    },

    getConsolidatedAccountsToPay: async (request: ConsolidatedToPayquest) => {
      try {
        return await acquaintApiClient.httpPost<ConsolidatedToPayItem[]>(
          `app/payments/GetConsolidatedAccountsToPay`,
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return [];
      }
    },

    payConsolidatedAccounts: async (request: PayAccountRequest) => {
      try {
        return await acquaintApiClient.httpPost<PaymentAccountItem>(
          `app/payments/payconsolidatedaccounts`,
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    produceConsolidatedStatements: async (request: BulkStatementRequest) => {
      try {
        return await acquaintApiClient.getFileWithPostData(
          `app/payments/produceconsolidatedstatements`,
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    clearedFunds: async (contactId: number, bankAccountId: number) => {
      try {
        return await acquaintApiClient.httpPost<boolean>(
          `app/payments/ClearedFunds`,
          { contactId, bankAccountId },
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return false;
      }
    },

    checkBankDetails: async (
      paymentExportFormat: number,
      bankAccountId: number,
    ) => {
      try {
        return await acquaintApiClient.httpPost<boolean>(
          `app/paymentruns/checkbankdetailsenteredpaymentfile`,
          { paymentExportFormat, bankAccountId },
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return false;
      }
    },

    fetchPaymentRunFile: async (paymentRunExportFileName: string) => {
      try {
        return await acquaintApiClient.getFileWithPostData(
          `app/paymentruns/fetchpaymentrunfile`,
          { paymentRunExportFileName },
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    paymentSummaryReport: async (
      bankAccountId: number,
      contactIds: number[],
    ) => {
      try {
        return await acquaintApiClient.getFileWithPostData(
          `app/payments/PaymentSummaryReport`,
          { bankAccountId, contactIds },
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    undoPaymentRuns: async (paymentRunId: number) => {
      try {
        await acquaintApiClient.httpPostVoid(
          `app/paymentruns/${paymentRunId}/undopaymentrunstatement`,
          null,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    fetchPaymentRunSummaryReport: async (
      request: any,
    ): Promise<Blob | undefined> => {
      try {
        return await acquaintApiClient.getFileWithPostData(
          "app/paymentruns/paymentrunsummaryreport",
          request,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return undefined;
      }
    },

    getContactDocumentIds: async (statementid: number) => {
      try {
        return await acquaintApiClient.httpGet<number[]>(
          `app/transactions/${statementid}/contactdocumentids`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return [];
      }
    },

    generateStatementInvoicePdf: async (
      contactId: number,
      statementId: number,
      companyId: number,
    ) => {
      try {
        return await acquaintApiClient.getFileWithPostData(
          `app/transactions/statementinvoicepdf`,
          {
            contactId,
            statementId,
            companyId,
          },
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },
  };
}
